import React, { useEffect, useRef, useState } from "react";

// SignaturePad — inline HTML5 canvas signature capture, no external deps.
//
// Returns the signed image as a base64 data URL via the onChange callback.
// The parent stores the data URL alongside typed-name + IP + user agent for
// the quote acceptance audit trail.
//
// UX notes:
//   - Pointer events (mouse + touch + pen unified) so it works on phones.
//   - Calls onChange("") when cleared so the parent can disable the
//     confirm button until the customer signs again.
//   - 2x DPR rendering so the saved data URL looks crisp when downloaded
//     or embedded in the tradie's invoice PDF.
export default function SignaturePad({ onChange, height = 160 }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  // Resize canvas to match its CSS width × DPR for crisp lines on retina.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const cssW = canvas.clientWidth;
      const cssH = height;
      if (cssW <= 0) return;
      canvas.width = cssW * ratio;
      canvas.height = cssH * ratio;
      canvas.style.height = `${cssH}px`;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.strokeStyle = "#1C1C1E";
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      // Clear after resize.
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, cssW, cssH);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [height]);

  function getPoint(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function handlePointerDown(e) {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  }

  function handlePointerMove(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPoint(e);
    const last = lastPointRef.current;
    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    lastPointRef.current = p;
    if (!hasInk) {
      setHasInk(true);
    }
  }

  function commit() {
    if (!hasInk) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  }

  function handlePointerUp(e) {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    canvasRef.current.releasePointerCapture?.(e.pointerId);
    commit();
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    setHasInk(false);
    onChange?.("");
  }

  return (
    <div>
      <div
        style={{
          position: "relative",
          width: "100%",
          background: "#FFFFFF",
          border: "1px dashed rgba(0,0,0,0.25)",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
          style={{
            display: "block",
            width: "100%",
            touchAction: "none",
            cursor: "crosshair",
          }}
          aria-label="Signature pad"
        />
        {!hasInk && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: "#9CA3AF",
              fontSize: "0.85rem",
            }}
          >
            Sign here with your finger or mouse
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "8px",
        }}
      >
        <span style={{ fontSize: "0.78rem", color: "#6B7280" }}>
          {hasInk ? "Signature captured" : "Required"}
        </span>
        <button
          type="button"
          onClick={handleClear}
          style={{
            background: "transparent",
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: "8px",
            padding: "5px 10px",
            fontSize: "0.78rem",
            cursor: "pointer",
            color: "#1C1C1E",
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
